import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import sb from '@/utils/sb'
import { supabaseAuthActions } from '@/utils/store/supabaseAuth'
import { type ActionFunctionArgs, Link, redirect, useFetcher, useSearchParams, useNavigate } from 'react-router'
import { useEffect, useState } from 'react'

export const action = async ({ request }: ActionFunctionArgs) => {
  const supabase = sb;
  const formData = await request.formData()
  const step = formData.get('step') as string

  // 步骤1：发送验证码
  if (step === 'send-otp') {
    const email = formData.get('email') as string

    if (!email) {
      return { error: '邮箱不能为空' }
    }

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
      },
    })

    if (error) {
      return { error: error.message }
    }

    return redirect(`/s_auth/sign-up?step=verify&email=${encodeURIComponent(email)}`)
  }

  // 步骤2：验证验证码并设置密码
  if (step === 'verify-otp') {
    const email = formData.get('email') as string
    const token = formData.get('token') as string
    const password = formData.get('password') as string
    const repeatPassword = formData.get('repeat-password') as string

    if (!token) {
      return { error: '验证码不能为空' }
    }

    if (!password) {
      return { error: '密码不能为空' }
    }

    if (password !== repeatPassword) {
      return { error: '密码不一致' }
    }

    // 验证 OTP
    const { data, error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    })

    if (verifyError) {
      return { error: verifyError.message }
    }

    // 验证成功后设置密码
    const { error: updateError } = await supabase.auth.updateUser({ 
      password 
    })

    if (updateError) {
      return { error: updateError.message }
    }

    // 更新认证状态
    await supabaseAuthActions.update();

    return redirect('/s_auth/sign-up?success')
  }

  return { error: '无效的操作' }
}

export default function SignUp() {
  const fetcher = useFetcher<typeof action>()
  let [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [countdown, setCountdown] = useState(3)

  const success = !!searchParams.has('success')
  const step = searchParams.get('step') || 'email'
  const email = searchParams.get('email') || ''
  const error = fetcher.data?.error
  const loading = fetcher.state === 'submitting'

  // 注册成功后3秒自动跳转到首页
  useEffect(() => {
    if (success) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer)
            navigate('/', { replace: true })
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [success, navigate])

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10 bg-gradient-to-b from-slate-50 to-gray-100">
      <div className="w-full max-w-sm">
        <div className="flex flex-col gap-6">
          {success ? (
            <Card className="shadow-lg border border-gray-100 bg-white">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-semibold tracking-tight text-emerald-600">注册成功！</CardTitle>
                <CardDescription className="text-gray-500">您的账户已创建并自动登录</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg mb-4">
                  <p className="text-sm text-emerald-700">
                    您已成功完成注册，{countdown} 秒后自动跳转到首页...
                  </p>
                </div>
                <Button 
                  className="w-full h-10 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium transition-colors" 
                  onClick={() => navigate('/', { replace: true })}
                >
                  立即前往首页
                </Button>
              </CardContent>
            </Card>
          ) : step === 'verify' ? (
            <Card className="shadow-lg border border-gray-100 bg-white">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-semibold tracking-tight">验证邮箱</CardTitle>
                <CardDescription className="text-gray-500">
                  我们已向 <span className="font-medium text-gray-700">{email}</span> 发送了验证码
                </CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="verify-otp" />
                  <input type="hidden" name="email" value={email} />
                  <div className="flex flex-col gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="token" className="text-sm font-medium text-gray-700">验证码</Label>
                      <Input
                        id="token"
                        name="token"
                        type="text"
                        placeholder="请输入6位验证码"
                        maxLength={6}
                        required
                        autoFocus
                        className="h-10 rounded-lg border-gray-200 focus:border-gray-400 focus:ring-gray-400/20 text-center text-lg tracking-widest"
                      />
                      <p className="text-xs text-gray-400">
                        请查看您的邮箱获取验证码
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="password" className="text-sm font-medium text-gray-700">密码</Label>
                      <Input 
                        id="password" 
                        name="password" 
                        type="password" 
                        placeholder="设置您的密码"
                        required 
                        className="h-10 rounded-lg border-gray-200 focus:border-gray-400 focus:ring-gray-400/20"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="repeat-password" className="text-sm font-medium text-gray-700">确认密码</Label>
                      <Input 
                        id="repeat-password" 
                        name="repeat-password" 
                        type="password" 
                        placeholder="再次输入密码"
                        required 
                        className="h-10 rounded-lg border-gray-200 focus:border-gray-400 focus:ring-gray-400/20"
                      />
                    </div>
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      className="w-full h-10 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium transition-colors mt-1" 
                      disabled={loading}
                    >
                      {loading ? '验证中...' : '验证并完成注册'}
                    </Button>
                  </div>
                  <div className="mt-5 text-center text-sm">
                    <Link 
                      to="/s_auth/sign-up" 
                      className="text-gray-500 hover:text-gray-700 underline underline-offset-4 transition-colors"
                    >
                      返回重新发送
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          ) : (
            <Card className="shadow-lg border border-gray-100 bg-white">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="text-2xl font-semibold tracking-tight">注册</CardTitle>
                <CardDescription className="text-gray-500">创建新账户</CardDescription>
              </CardHeader>
              <CardContent>
                <fetcher.Form method="post">
                  <input type="hidden" name="step" value="send-otp" />
                  <div className="flex flex-col gap-5">
                    <div className="grid gap-2">
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">邮箱</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="m@example.com"
                        defaultValue={email}
                        required
                        autoFocus
                        className="h-10 rounded-lg border-gray-200 focus:border-gray-400 focus:ring-gray-400/20"
                      />
                      <p className="text-xs text-gray-400">
                        我们将向此邮箱发送验证码
                      </p>
                    </div>
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                        <p className="text-sm text-red-600">{error}</p>
                      </div>
                    )}
                    <Button 
                      type="submit" 
                      className="w-full h-10 rounded-lg bg-gray-900 hover:bg-gray-800 text-white font-medium transition-colors" 
                      disabled={loading}
                    >
                      {loading ? '发送中...' : '发送验证码'}
                    </Button>
                  </div>
                  <div className="mt-5 text-center text-sm text-gray-500">
                    已有账户？{' '}
                    <Link to="/s_auth/login" className="text-gray-700 font-medium underline underline-offset-4 hover:text-gray-900 transition-colors">
                      登录
                    </Link>
                  </div>
                </fetcher.Form>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
